import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/sr';
import 'dayjs/locale/en-gb';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

import en from './en/common.json';
import sr from './sr/common.json';

dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(utc);

/**
 * Two language identities have to stay in step: the short i18next key and the tag Django
 * knows. Django's is `sr-latn-me` - Serbian in Latin script, ijekavica as written in
 * Montenegro - which is too long to want in filenames, so the catalog directory is `sr`.
 */
export const LANGUAGES = [
  { key: 'sr', djangoTag: 'sr-latn-me', label: 'Srpski', flag: '🇲🇪' },
  { key: 'en', djangoTag: 'en', label: 'English', flag: '🇬🇧' },
] as const;

export type LanguageKey = (typeof LANGUAGES)[number]['key'];

export const DEFAULT_LANGUAGE: LanguageKey = 'sr';

/** Django reads this cookie on every request, so the API answers in the same language as the UI. */
const DJANGO_LANGUAGE_COOKIE = 'django_language';
const STORAGE_KEY = 'fly.language';

const DAYJS_LOCALES: Record<LanguageKey, string> = {
  sr: 'sr',
  en: 'en-gb',
};

export function djangoTagFor(key: string): string {
  return LANGUAGES.find((language) => language.key === key)?.djangoTag ?? 'en';
}

/** Maps anything the browser or Django might hand us onto one of our two catalogs. */
export function normaliseLanguage(value: string | null | undefined): LanguageKey {
  if (!value) return DEFAULT_LANGUAGE;
  const lower = value.toLowerCase();
  if (lower.startsWith('sr') || lower.startsWith('hr') || lower.startsWith('bs')) return 'sr';
  if (lower.startsWith('en')) return 'en';
  return DEFAULT_LANGUAGE;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function detectLanguage(): LanguageKey {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored) return normaliseLanguage(stored);

  const fromDjango = readCookie(DJANGO_LANGUAGE_COOKIE);
  if (fromDjango) return normaliseLanguage(fromDjango);

  if (typeof navigator !== 'undefined') {
    for (const candidate of navigator.languages ?? [navigator.language]) {
      const lower = candidate.toLowerCase();
      if (lower.startsWith('sr') || lower.startsWith('en')) return normaliseLanguage(lower);
    }
  }
  return DEFAULT_LANGUAGE;
}

export const resources = {
  en: { common: en },
  sr: { common: sr },
} as const;

void i18next.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: ['common'],
  interpolation: { escapeValue: false },
  returnNull: false,
});

applyLocaleSideEffects(i18next.language);
i18next.on('languageChanged', applyLocaleSideEffects);

function applyLocaleSideEffects(language: string) {
  const key = normaliseLanguage(language);
  dayjs.locale(DAYJS_LOCALES[key]);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = djangoTagFor(key);
  }
}

/**
 * Switching language has to persist in three places: i18next for the UI, localStorage so a
 * reload remembers, and the Django cookie so server-rendered text and translated database
 * content - taxonomy names, organisation names - come back in the same language.
 */
export async function setLanguage(value: string) {
  const key = normaliseLanguage(value);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, key);
  }
  if (typeof document !== 'undefined') {
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `${DJANGO_LANGUAGE_COOKIE}=${djangoTagFor(key)}; path=/; max-age=${oneYear}; samesite=lax`;
  }
  await i18next.changeLanguage(key);
}

export default i18next;
